/**
 * TrialRequestDialog - Modal for requesting a trial account.
 * Collects name + email, sends to /api/trial/request, shows confirmation.
 */
import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { API, toast } from '@/App';
import { Clock, CheckCircle2 } from 'lucide-react';

export default function TrialRequestDialog({ open, onOpenChange }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/trial/request`, { name: name.trim(), email: email.trim() });
      setSubmitted(true);
      toast.success('Trial request submitted!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || 'Could not submit trial request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (next) => {
    onOpenChange(next);
    if (!next) {
      // reset after close transition
      setTimeout(() => {
        setName('');
        setEmail('');
        setSubmitted(false);
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="trial-request-dialog"
        style={{ maxWidth: 'min(95vw, 460px)', width: '95vw', padding: '1.5rem' }}
      >
        {submitted ? (
          <div data-testid="trial-request-confirmation" style={{ textAlign: 'center', padding: '0.5rem 0 0.25rem' }}>
            <CheckCircle2 size={56} color="#10b981" style={{ margin: '0 auto 0.75rem' }} aria-hidden="true" />
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0030b3', margin: '0 0 0.5rem' }}>
              Request received!
            </h3>
            <p style={{ fontSize: '0.95rem', color: '#374151', lineHeight: 1.55, margin: '0 0 1rem' }}>
              We&rsquo;ll email your trial-account login credentials to{' '}
              <strong style={{ color: '#0030b3' }}>{email}</strong> within the next 24 hours.
            </p>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 1.25rem' }}>
              Please check your inbox (and spam folder, just in case).
            </p>
            <Button
              type="button"
              className="w-full"
              onClick={() => handleClose(false)}
              data-testid="trial-request-close"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Request a Trial Account</DialogTitle>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem', lineHeight: 1.5 }}>
                Tell us who you are. We&rsquo;ll set up a trial account and email your
                login credentials within 24 hours.
              </p>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4" style={{ marginTop: '0.5rem' }}>
              <div>
                <Label htmlFor="trial-name">Full Name</Label>
                <Input
                  id="trial-name"
                  type="text"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="trial-name-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="trial-email">Email</Label>
                <Input
                  id="trial-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="trial-email-input"
                  required
                />
              </div>

              <div
                style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  padding: '0.75rem 0.9rem',
                  display: 'flex',
                  gap: '0.6rem',
                  alignItems: 'flex-start',
                }}
              >
                <Clock size={18} color="#1d4ed8" style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
                <p style={{ fontSize: '0.85rem', color: '#1e3a8a', margin: 0, lineHeight: 1.5 }}>
                  Your trial-account login credentials will be emailed to the address above
                  within <strong>24 hours</strong>.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="trial-request-submit"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
